/** Whether a freshly hydrated profile actually says anything new (#927).
 *
 *  `hydrateUserFromBackend` runs on every `TOKEN_REFRESHED` — a routine
 *  credential rotation, not a change of user — and published a brand-new
 *  object each time, so every `useAuth` consumer re-rendered over a profile
 *  that had not changed. On an open takeoff drawing that churn is visible: the
 *  reported symptom is a screen that "keeps refreshing for no reason".
 *
 *  A key-by-key comparison is the whole job because the auth user row is flat:
 *  a spread of the `users` row plus scalar aliases for its snake/camel pairs
 *  (see normalizeAuthUserRow). Anything non-scalar compares by reference and
 *  so reads as changed, which is the safe direction — a real change must never
 *  be swallowed, while an unnecessary publish only costs what it cost before.
 */
export function sameAuthUser(
  current: Record<string, unknown> | null | undefined,
  next: Record<string, unknown>,
): boolean {
  if (!current) return false;
  // Both key sets, so a field appearing or disappearing counts as a change —
  // a profile persisted by an older build can be missing one the row has.
  for (const key of new Set([...Object.keys(current), ...Object.keys(next)])) {
    if (current[key] !== next[key]) return false;
  }
  return true;
}
